import type { CoreModel, ModelProperty } from '@salescore/core'
import { Array, pipe, Record } from 'effect'
import { useCallback, useMemo } from 'react'
import { useRecoilValue } from 'recoil'

import { buildConvertedSheetSheetCustomModels, sheetCustomModelsAtom, viewsContextAtom } from '../atoms'

const useModelMapper = () => {
  const { models } = useRecoilValue(viewsContextAtom)
  const sheetCustomModels = useRecoilValue(sheetCustomModelsAtom)
  const convertedSheetCustomModels = useMemo(
    () => buildConvertedSheetSheetCustomModels(sheetCustomModels),
    [sheetCustomModels],
  )
  return useMemo(
    () =>
      pipe(
        [...models, ...convertedSheetCustomModels],
        Array.groupBy((model) => model.name),
        Record.map((values) => {
          const [model] = values
          const propertyMapper = Record.fromIterableBy(Array.reverse(model.properties), (x) => x.name)
          return {
            model,
            propertyMapper,
          }
        }),
      ),
    [convertedSheetCustomModels, models],
  )
}

export const useGetModelProperty = (): ((
  modelName: string | undefined,
  propertyName: string | undefined,
) => undefined | ModelProperty) => {
  const modelMapper = useModelMapper()

  return useCallback(
    (modelName: string | undefined, propertyName: string | undefined) => {
      if (modelName === undefined || propertyName === undefined) {
        return
      }
      const propertyMapper = modelMapper[modelName]?.propertyMapper
      if (propertyMapper === undefined) {
        return
      }
      return propertyMapper[propertyName]
    },
    [modelMapper],
  )
}
export const useGetModelAndProperty = (): ((
  modelName: string,
  propertyName: string,
) =>
  | undefined
  | {
      model: CoreModel
      property: ModelProperty
    }) => {
  const modelMapper = useModelMapper()

  return useCallback(
    (modelName: string, propertyName: string) => {
      const model = modelMapper[modelName]
      if (model === undefined) {
        return
      }
      const property = model.propertyMapper[propertyName]
      if (property === undefined) {
        return
      }
      return {
        model: model.model,
        property,
      }
    },
    [modelMapper],
  )
}

export const useGetModel = (): ((modelName: string | undefined) => undefined | CoreModel) => {
  const modelMapper = useModelMapper()

  return useCallback(
    (modelName: string | undefined) => {
      if (modelName === undefined) {
        return
      }
      return modelMapper[modelName]?.model
    },
    [modelMapper],
  )
}
